<template>
  <div
    v-show="!layoutHidden"
    class="consent"
    :style="{'--consent-inset': contentInset + 'px'}"
    data-private>
    <transition name="consent-slide">
      <section
        v-if="visible && pageReady"
        ref="panel"
        class="consent-panel"
        :class="{
          'consent-expanded': expanded,
          'consent-narrow': contentWidth <= 1100,
          'consent-stacked': contentWidth <= 800,
          'consent-mobile': contentWidth <= 640,
        }"
        role="dialog"
        aria-labelledby="consent-title"
        aria-describedby="consent-description"
        tabindex="-1"
        @keydown.esc="dismiss">
        <div class="consent-summary">
          <div v-if="!expanded" class="consent-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 3 4.5 6v5.3c0 4.2 2.8 7.9 7.5 9.7 4.7-1.8 7.5-5.5 7.5-9.7V6L12 3Z" />
              <path d="m8.5 12 2.3 2.3 4.7-4.8" />
            </svg>
          </div>
          <div class="consent-copy">
            <h2 id="consent-title">{{ expanded ? 'Privacy preferences' : 'Your privacy, your choice' }}</h2>
            <p v-if="expanded" id="consent-description" class="consent-intro">
              Manage your optional data preferences.
            </p>
            <p v-else id="consent-description" class="consent-intro">
              Optional usage data helps us improve your experience.
              Analytics may be linked to your account when you sign in. Essential features always stay on.
            </p>
          </div>
        </div>
        <button type="button" class="consent-close" aria-label="Close privacy preferences" :disabled="saving" @click="dismiss">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" /></svg>
        </button>

        <div v-if="expanded" id="consent-choices" class="consent-choices">
          <div class="consent-choice consent-necessary">
            <div class="choice-copy">
              <h3>Necessary</h3>
              <p>
                Required for core site functions, including secure sign-in and saving your privacy preferences.
                This category stays active so the service can operate.
              </p>
            </div>
            <span class="necessary-status">Always active</span>
          </div>

          <label class="consent-choice" :class="{'choice-enabled': analytics}">
            <div class="choice-copy">
              <h3>Product analytics</h3>
              <p>
                Usage and error data help us understand product performance and troubleshoot issues.
                This data may be linked to your account after sign-in.
              </p>
            </div>
            <span class="consent-switch">
              <input v-model="analytics" type="checkbox" role="switch" aria-label="Allow product analytics" :disabled="saving" />
              <span class="switch-track" aria-hidden="true"></span>
            </span>
          </label>

          <label class="consent-choice" :class="{'choice-enabled': sessionReplay}">
            <div class="choice-copy">
              <h3>Experience improvements</h3>
              <p>
                Page interaction playback and technical performance data help us improve usability.
                Device names and operational information may be visible. Inputs and selected sensitive
                content are masked, and sensitive screens are excluded.
              </p>
            </div>
            <span class="consent-switch">
              <input v-model="sessionReplay" type="checkbox" role="switch" aria-label="Allow experience improvements" :disabled="saving" />
              <span class="switch-track" aria-hidden="true"></span>
            </span>
          </label>
        </div>

        <div class="consent-actions" :aria-busy="saving">
          <button
            v-if="!expanded"
            type="button"
            class="consent-button secondary consent-customize"
            aria-expanded="false"
            aria-controls="consent-choices"
            :disabled="saving"
            @click="customize">Customize</button>
          <button v-if="expanded" type="button" class="consent-button secondary consent-deny" :disabled="saving" @click="save(false, false)">Reject all</button>
          <button type="button" class="consent-button secondary consent-accept" :disabled="saving" @click="save(true, true)">Accept all</button>
          <button
            v-if="expanded"
            type="button"
            class="consent-button secondary consent-save"
            :disabled="saving"
            @click="save(analytics, sessionReplay)">{{ saving ? 'Saving…' : 'Save preferences' }}</button>
        </div>

        <div class="consent-footnote">
          <a href="https://sixfab.com/privacy/" target="_blank" rel="noopener noreferrer">Privacy policy <span aria-hidden="true">↗</span></a>
          <span>When signed in, your saved choices apply across devices.</span>
          <button v-if="expanded" type="button" class="consent-back" :disabled="saving" @click="collapse">Back</button>
          <p v-if="saveError" class="consent-error" role="alert">{{ saveError }}</p>
          <p v-if="currentConsent.analytics" class="consent-refresh" role="status">
            Turning off analytics refreshes this page to stop tracking.
          </p>
        </div>
      </section>
    </transition>
  </div>
</template>

<script>
import {getConsent, saveConsent, subscribeConsent, requiresConsentReload} from "../services/consent";

export default {
  name: "ConsentPreferences",
  data() {
    const preferences = getConsent();
    return {
      visible: !preferences.decided,
      expanded: false,
      analytics: preferences.decided ? preferences.analytics : true,
      sessionReplay: preferences.decided ? preferences.sessionReplay : true,
      currentConsent: preferences,
      previousFocus: null,
      unsubscribe: null,
      contentInset: 0,
      contentWidth: window.innerWidth,
      layoutHidden: false,
      layoutObserver: null,
      pageObserver: null,
      documentLoaded: document.readyState === "complete",
      contentLoading: true,
      saving: false,
      saveError: "",
    };
  },
  computed: {
    pageReady() {
      return this.documentLoaded
        && !this.$store.getters.isLoading
        && !this.contentLoading
        && this.$route.matched.length > 0
        && this.$route.name !== "login";
    },
  },
  watch: {
    visible(shown) {
      if (shown) this.syncPageLoading();
    },
    $route() {
      this.contentLoading = true;
      this.$nextTick(() => {
        this.observeLayout();
        this.syncPageLoading();
      });
    },
    pageReady(ready) {
      if (ready) this.$nextTick(this.observeLayout);
    },
  },
  mounted() {
    window.addEventListener("sixfab:open-consent", this.open);
    window.addEventListener("resize", this.syncLayout);
    window.addEventListener("load", this.onDocumentLoad);
    const app = document.getElementById("app");
    if (app) {
      this.pageObserver = new MutationObserver(this.syncPageLoading);
      this.pageObserver.observe(app, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style", "hidden", "aria-busy"],
      });
    }
    this.$nextTick(() => {
      this.observeLayout();
      this.syncPageLoading();
    });
    this.unsubscribe = subscribeConsent((next, previous, metadata = {}) => {
      if (metadata.source === "pending") return;
      this.currentConsent = next;
      this.analytics = next.decided ? next.analytics : true;
      this.sessionReplay = next.decided ? next.sessionReplay : true;
      if (metadata.source === "account") {
        this.visible = !next.decided;
        this.expanded = false;
        this.saveError = "";
      }
      if (requiresConsentReload(next, previous) && !metadata.skipReload) window.location.reload();
    });
  },
  beforeDestroy() {
    window.removeEventListener("sixfab:open-consent", this.open);
    window.removeEventListener("resize", this.syncLayout);
    window.removeEventListener("load", this.onDocumentLoad);
    if (this.layoutObserver) this.layoutObserver.disconnect();
    if (this.pageObserver) this.pageObserver.disconnect();
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    onDocumentLoad() {
      this.documentLoaded = true;
      this.syncPageLoading();
    },
    syncPageLoading() {
      if (!this.visible) return;
      // Route resolution finishes before cards and other page content finish loading.
      const app = document.getElementById("app");
      const indicators = app ? app.querySelectorAll(".loading-overlay, .spinner, [aria-busy='true']") : [];
      this.contentLoading = Array.from(indicators).some((element) => (
        !element.closest(".consent") && element.getClientRects().length > 0
      ));
    },
    syncLayout() {
      const content = document.querySelector(".six-layout .row > .content");
      const bounds = content ? content.getBoundingClientRect() : null;
      this.contentInset = bounds ? Math.max(0, bounds.left) : 0;
      this.contentWidth = Math.max(0, window.innerWidth - this.contentInset);
      this.layoutHidden = !!bounds && (bounds.width <= 0 || this.contentWidth <= 0);
    },
    observeLayout() {
      if (this.layoutObserver) this.layoutObserver.disconnect();
      this.syncLayout();
      const content = document.querySelector(".six-layout .row > .content");
      if (content && window.ResizeObserver) {
        this.layoutObserver = new window.ResizeObserver(this.syncLayout);
        this.layoutObserver.observe(content);
      }
    },
    open() {
      const preferences = getConsent();
      this.analytics = preferences.decided ? preferences.analytics : true;
      this.sessionReplay = preferences.decided ? preferences.sessionReplay : true;
      this.previousFocus = document.activeElement;
      this.saveError = "";
      this.expanded = true;
      this.visible = true;
      this.$nextTick(() => { if (this.$refs.panel) this.$refs.panel.focus(); });
    },
    customize() {
      this.expanded = true;
      this.$nextTick(() => { if (this.$refs.panel) this.$refs.panel.focus(); });
    },
    collapse() {
      this.expanded = false;
      this.$nextTick(() => { if (this.$refs.panel) this.$refs.panel.focus(); });
    },
    dismiss() {
      if (this.saving) return;
      this.visible = false;
      if (this.previousFocus && this.previousFocus.focus) this.previousFocus.focus();
    },
    async save(analytics, sessionReplay) {
      if (this.saving) return;
      this.saving = true;
      this.saveError = "";
      try {
        await saveConsent({analytics, sessionReplay});
        this.saving = false;
        this.dismiss();
      } catch (error) {
        this.saveError = "We couldn't save your preferences. Please try again.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.consent { font-family: "Inter", sans-serif; color: #172b3a; line-height: 1.5; }
.consent-panel {
  position: fixed; bottom: 20px; left: calc(var(--consent-inset, 0px) + 20px); right: 20px; z-index: 2147483600;
  display: grid; grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas: "summary actions" "footnote actions"; column-gap: 32px; row-gap: 12px;
  max-height: calc(100vh - 40px); max-height: calc(100dvh - 40px);
  overflow-y: auto; padding: 24px 56px 22px 24px; border-radius: 16px; background: #fff;
  border: 1px solid #c8d0d8;
  box-shadow: 0 4px 20px rgba(24, 32, 40, .1), 0 12px 32px rgba(24, 32, 40, .12);
  box-sizing: border-box; text-align: left;
}
.consent-summary { grid-area: summary; display: flex; align-items: flex-start; gap: 14px; min-width: 0; }
.consent-copy { max-width: 780px; min-width: 0; }
.consent-mark { flex: 0 0 36px; height: 36px; border-radius: 10px; background: #eef1f4; display: grid; place-items: center; }
.consent-mark svg { width: 21px; height: 21px; stroke: #526272; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; }
.consent-close { position: absolute; top: 12px; right: 12px; border: 0; background: transparent; width: 32px; height: 32px; display: grid; place-items: center; border-radius: 8px; cursor: pointer; }
.consent-close:hover { background: #f0f4f6; }
.consent-close svg { width: 17px; height: 17px; stroke: #647886; stroke-width: 1.6; stroke-linecap: round; }
.consent-panel h2 { margin: 0 0 7px; font-size: 18px; font-weight: 600; letter-spacing: -.35px; line-height: 1.3; color: #26313d; }
.consent-intro { margin: 0; color: #5e6a76; font-size: 12px; line-height: 1.7; }
.consent-expanded { grid-template-areas: "summary summary" "details details" "footnote actions"; row-gap: 12px; padding: 16px 48px 14px 20px; border-radius: 10px; }
.consent-expanded h2 { margin-bottom: 4px; font-size: 16px; letter-spacing: -.2px; }
.consent-expanded .consent-intro { font-size: 11px; }
.consent-expanded .consent-footnote { margin-left: 0; }
.consent-expanded .consent-actions { grid-template-columns: repeat(3, 132px); gap: 8px; }
.consent-expanded .consent-button { min-height: 34px; padding: 8px 12px; border-radius: 6px; font-size: 11px; }
.consent-choices { grid-area: details; border: 1px solid #e1e7ec; border-radius: 6px; overflow: hidden; background: #fff; }
.consent-choice { display: grid; grid-template-columns: 170px minmax(0, 1fr) 80px; align-items: center; gap: 16px; min-height: 44px; padding: 10px 12px; border-bottom: 1px solid #e8edf1; cursor: pointer; box-sizing: border-box; transition: background .15s; }
.consent-choice:last-child { border-bottom: 0; }
.consent-choice:hover, .consent-choice:focus-within { background: #f7f9fb; }
.consent-necessary { cursor: default; background: #fafbfc; }
.consent-necessary:hover { background: #fafbfc; }
.necessary-status { justify-self: end; font-size: 10px; color: #687b86; font-weight: 500; white-space: nowrap; }
.choice-enabled { background: #f7fafb; }
.choice-copy { display: contents; }
.choice-copy h3 { margin: 0; font-size: 12px; line-height: 1.5; font-weight: 600; }
.choice-copy p { margin: 0; font-size: 11px; line-height: 1.5; color: #687b86; }
.consent-switch { position: relative; width: 34px; height: 20px; justify-self: end; }
.consent-switch input { position: absolute; inset: 0; opacity: 0; width: 100%; height: 100%; cursor: pointer; margin: 0; z-index: 1; }
.switch-track { position: absolute; inset: 0; border-radius: 20px; background: #d5dfe5; transition: background .15s; }
.switch-track::after { content: ""; position: absolute; top: 3px; left: 3px; width: 14px; height: 14px; background: #fff; border-radius: 50%; box-shadow: 0 1px 3px #0002; transition: transform .15s; }
.consent-switch input:checked + .switch-track { background: #246680; }
.consent-switch input:checked + .switch-track::after { transform: translateX(14px); }
.consent-switch input:focus-visible + .switch-track { outline: 3px solid #7eb6d0; outline-offset: 3px; }
.consent-refresh { flex-basis: 100%; font-size: 11px; color: #7e591b; margin: 0; }
.consent-error { flex-basis: 100%; font-size: 11px; color: #a33333; margin: 0; }
.consent-actions { grid-area: actions; display: grid; grid-template-columns: repeat(2, 144px); align-items: center; gap: 10px; align-self: center; }
.consent-expanded .consent-actions { grid-template-columns: repeat(3, 144px); }
.consent-button { min-height: 42px; min-width: 0; padding: 11px 16px; border-radius: 9px; font-family: inherit; font-size: 12px; font-weight: 600; cursor: pointer; transition: background .15s; white-space: nowrap; }
.consent-button.secondary { border: 1px solid #bec7d0; background: #f7f8fa; color: #26313d; }
.consent-button.secondary:hover { background: #ebeff3; }
.consent button:disabled { opacity: .6; cursor: wait; }
.consent-switch input:disabled { cursor: wait; }
.consent button:focus-visible, .consent a:focus-visible { outline: 3px solid #7eb6d0; outline-offset: 3px; }
.consent-footnote { grid-area: footnote; display: flex; align-items: center; flex-wrap: wrap; gap: 6px 16px; margin-left: 50px; font-size: 10px; color: #73838d; }
.consent-footnote a { color: #52758a; text-decoration: none; white-space: nowrap; }
.consent-footnote a:hover { text-decoration: underline; }
.consent-back { padding: 0; border: 0; background: transparent; color: #52758a; font: inherit; text-decoration: underline; cursor: pointer; }
.consent-slide-enter-active, .consent-slide-leave-active { transition: opacity .18s, transform .18s; }
.consent-slide-enter, .consent-slide-leave-to { opacity: 0; transform: translateY(12px); }
.consent-narrow { grid-template-areas: "summary summary" "footnote actions"; column-gap: 20px; }
.consent-narrow.consent-expanded { grid-template-areas: "summary summary" "details details" "footnote actions"; }
.consent-narrow .consent-footnote { margin-left: 0; }
.consent-stacked { grid-template-columns: minmax(0, 1fr); grid-template-areas: "summary" "actions" "footnote"; }
.consent-stacked.consent-expanded { grid-template-areas: "summary" "details" "actions" "footnote"; }
.consent-stacked .consent-actions { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.consent-stacked.consent-expanded .consent-actions { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.consent-mobile { bottom: 0; left: var(--consent-inset, 0px); right: 0; padding: 20px 16px calc(16px + env(safe-area-inset-bottom, 0px)); border-radius: 16px 16px 0 0; grid-template-columns: minmax(0, 1fr); grid-template-areas: "summary" "actions" "footnote"; gap: 16px; max-height: calc(100vh - 16px); max-height: calc(100dvh - 16px); }
.consent-mobile.consent-expanded { grid-template-areas: "summary" "details" "actions" "footnote"; padding: 14px 16px calc(12px + env(safe-area-inset-bottom, 0px)); border-radius: 10px 10px 0 0; gap: 10px; }
.consent-mobile .consent-summary { gap: 10px; }
.consent-mobile .consent-mark { flex-basis: 30px; height: 30px; }
.consent-mobile h2 { margin-right: 30px; font-size: 17px; }
.consent-mobile.consent-expanded h2 { font-size: 16px; }
.consent-mobile .consent-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.consent-mobile.consent-expanded .consent-actions { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.consent-mobile .consent-save { grid-column: 1 / -1; }
.consent-mobile .consent-button { min-width: 0; padding: 10px 6px; font-size: 11px; }
.consent-mobile .consent-choice { grid-template-columns: minmax(0, 1fr) auto; grid-template-areas: "title control" "description description"; padding: 8px 10px; gap: 3px 12px; }
.consent-mobile .choice-copy h3 { grid-area: title; }
.consent-mobile .choice-copy p { grid-area: description; }
.consent-mobile .consent-switch, .consent-mobile .necessary-status { grid-area: control; }
@media (prefers-reduced-motion: reduce) {
  .consent-slide-enter-active, .consent-slide-leave-active, .consent-choice, .switch-track, .switch-track::after { transition: none; }
}
</style>
